import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { api } from '@/app/api';
import {
  fetchSettings,
  selectSettingsGroup,
  updateSettingItem,
} from '@main/features/settings/settingsSlice';
import { UiButton, UiCheckbox, UiInput } from '@shared/common/ui';

const GROUP = 'telegram_broadcasts';
const HELPER_KEY = 'helper';
const EMPTY_CHAT = { title: '', chatId: '' };

function errorMessage(error, fallback = 'Не удалось выполнить запрос') {
  return error?.response?.data?.message ?? error?.message ?? fallback;
}

/**
 * Справочник Telegram-групп для ежедневных опросов посещаемости.
 * Чаты заводятся вручную: токен бота здесь не хранится.
 */
export function TelegramGroupsSettings() {
  const dispatch = useDispatch();
  const settings = useSelector(selectSettingsGroup(GROUP));
  const [botDraft, setBotDraft] = useState(null);
  const [botSaving, setBotSaving] = useState(false);
  const [chats, setChats] = useState([]);
  const [chatsStatus, setChatsStatus] = useState('loading');
  const [chatsError, setChatsError] = useState('');
  const [chatForm, setChatForm] = useState(EMPTY_CHAT);
  const [chatSaving, setChatSaving] = useState(false);
  const [chatFormError, setChatFormError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (settings.status === 'idle') dispatch(fetchSettings(GROUP));
  }, [dispatch, settings.status]);

  const savedBot = useMemo(() => {
    const item = settings.items.find((it) => it.key === HELPER_KEY);
    return {
      botAlias: item?.value?.bot_alias?.trim() || 'helper',
      enabled: item?.value?.enabled !== false,
    };
  }, [settings.items]);

  const botForm = botDraft ?? savedBot;

  const loadChats = useCallback(async () => {
    setChatsStatus('loading');
    setChatsError('');
    try {
      const { data } = await api.get('/owner/telegram/helper-chats');
      setChats(data.items ?? []);
      setChatsStatus('ready');
    } catch (err) {
      setChatsError(errorMessage(err, 'Не удалось загрузить список чатов'));
      setChatsStatus('error');
    }
  }, []);

  useEffect(() => {
    void loadChats();
  }, [loadChats]);

  const onSaveBot = async () => {
    setBotSaving(true);
    const result = await dispatch(
      updateSettingItem({
        group: GROUP,
        key: HELPER_KEY,
        value: {
          bot_alias: botForm.botAlias.trim() || 'helper',
          enabled: botForm.enabled,
        },
      }),
    );
    setBotSaving(false);
    if (result.meta.requestStatus === 'fulfilled') setBotDraft(null);
  };

  const onAddChat = async (event) => {
    event.preventDefault();
    setChatFormError('');
    const title = chatForm.title.trim();
    const chatId = chatForm.chatId.trim();
    if (!title || !chatId) {
      setChatFormError('Укажите название и Chat ID');
      return;
    }
    setChatSaving(true);
    try {
      const { data } = await api.post('/owner/telegram/helper-chats', { title, chatId });
      setChats((prev) => [...prev, data.item].sort((a, b) => a.title.localeCompare(b.title, 'ru')));
      setChatForm(EMPTY_CHAT);
    } catch (err) {
      setChatFormError(errorMessage(err, 'Не удалось добавить чат'));
    } finally {
      setChatSaving(false);
    }
  };

  const onDeleteChat = async (item) => {
    const confirmed = window.confirm(
      `Удалить «${item.title}»? У направлений, где выбрана эта группа, привязка будет сброшена.`,
    );
    if (!confirmed) return;
    setDeletingId(item.id);
    setChatsError('');
    try {
      await api.delete(`/owner/telegram/helper-chats/${item.id}`);
      setChats((prev) => prev.filter((it) => it.id !== item.id));
    } catch (err) {
      setChatsError(errorMessage(err, 'Не удалось удалить чат'));
    } finally {
      setDeletingId(null);
    }
  };

  if (settings.status === 'loading' && chatsStatus === 'loading') {
    return <div className="placeholder">Загрузка…</div>;
  }

  return (
    <div className="telegram-groups-settings">
      <div className="telegram-groups-settings__row">
      <div className="form-card">
        <h2>Бот помощника</h2>
        <p className="muted">
          Алиас бота на proxy (по умолчанию helper). Токен в кабинете не хранится.
          Если ежедневные опросы выключены, cron ничего не отправит этому кабинету.
        </p>

        {settings.error && <div className="banner banner--danger">{settings.error}</div>}
        {settings.saveError && <div className="banner banner--danger">{settings.saveError}</div>}

        <UiInput
          label="Алиас бота"
          value={botForm.botAlias}
          onChange={(e) => setBotDraft({ ...botForm, botAlias: e.target.value })}
        />
        <UiCheckbox
          label="Ежедневные опросы посещаемости включены"
          checked={botForm.enabled}
          onChange={(e) => setBotDraft({ ...botForm, enabled: e.target.checked })}
        />
        <div className="form-actions">
          <UiButton type="button" disabled={botSaving || !botDraft} onClick={onSaveBot}>
            {botSaving ? 'Сохраняем…' : 'Сохранить'}
          </UiButton>
        </div>
      </div>

      <div className="form-card">
        <h2>Группы</h2>
        <p className="muted">
          Chat ID группы можно взять из обновления @userinfobot. Бот Helper уже должен
          быть добавлен в эту группу и иметь право отправлять опросы.
        </p>

        <form onSubmit={onAddChat}>
          <div className="telegram-groups-settings__fields">
            <UiInput
              label="Название"
              value={chatForm.title}
              maxLength={255}
              onChange={(e) => setChatForm({ ...chatForm, title: e.target.value })}
            />
            <UiInput
              label="Chat ID"
              value={chatForm.chatId}
              maxLength={32}
              onChange={(e) => setChatForm({ ...chatForm, chatId: e.target.value })}
            />
          </div>
          {chatFormError && <div className="banner banner--danger">{chatFormError}</div>}
          <div className="form-actions">
            <UiButton type="submit" disabled={chatSaving}>
              {chatSaving ? 'Добавляем…' : 'Добавить'}
            </UiButton>
          </div>
        </form>
      </div>
      </div>

      <div>
        <h2 style={{ marginTop: 0 }}>Добавленные чаты</h2>
        {chatsError && <div className="banner banner--danger">{chatsError}</div>}
        {chatsStatus === 'loading' ? (
          <div className="placeholder">Загрузка…</div>
        ) : chats.length === 0 ? (
          <div className="placeholder">Чатов пока нет. Добавьте группу вручную.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Chat ID</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {chats.map((item) => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td className="muted">{item.chatId}</td>
                  <td>
                    <UiButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={deletingId === item.id}
                      onClick={() => onDeleteChat(item)}
                    >
                      {deletingId === item.id ? 'Удаляем…' : 'Удалить'}
                    </UiButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
